/* Phase 19 tail -- Home dashboard tile: "Open action items across meetings".
 *
 * Aggregates the current user's open + in_progress action items across every
 * meeting they attend (or, for CEO/ADMIN, across the whole workspace). Uses
 * the existing `GET /api/meetings/action-items/mine` endpoint (Phase 20 add).
 * Clicking a row opens the source meeting.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ListChecks, CalendarClock, Loader2, ArrowUpRight, User } from "lucide-react";
import api from "@/lib/api";
import GlassSurface from "@/components/ui/GlassSurface";
import { Badge } from "@/components/ui/badge";

const PRIORITY_STYLES = {
  P0: "text-rose-300 bg-rose-500/10 border-rose-500/40",
  P1: "text-amber-300 bg-amber-500/10 border-amber-500/40",
  P2: "text-sky-300 bg-sky-500/10 border-sky-500/40",
  P3: "text-foreground/60 bg-white/[0.03] border-white/10",
};

export default function MeetingActionsTile() {
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const { data } = await api.get("/meetings/action-items/mine", {
          params: { status: "open,in_progress", limit: 12 },
        });
        if (live) setItems(data.items || []);
      } catch (_e) {
        if (live) setItems([]);
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, []);

  const count = items?.length ?? 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      data-testid="meeting-actions-tile"
    >
      <GlassSurface variant="card" className="p-5 space-y-3">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-sky-500/25 to-blue-700/10 border border-sky-400/30 flex items-center justify-center">
            <ListChecks className="h-4 w-4 text-sky-300" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-heading text-xs uppercase tracking-[0.18em] text-foreground/60">
              Meeting action items
            </div>
            <div className="font-heading text-2xl leading-tight">
              {loading ? "—" : count}
              <span className="text-xs text-foreground/50 ml-2 tracking-wide">open across all meetings</span>
            </div>
          </div>
          <button
            onClick={() => nav("/meetings")}
            className="text-[11px] text-sky-300 hover:text-sky-200 flex items-center gap-1"
            data-testid="meeting-actions-tile-see-all"
          >
            All meetings <ArrowUpRight className="h-3 w-3" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-xs text-foreground/50 py-3">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading action items…
          </div>
        ) : count === 0 ? (
          <div className="text-xs text-foreground/55 py-2" data-testid="meeting-actions-tile-empty">
            Nothing on your plate. Fresh air — go pick a fight worth winning.
          </div>
        ) : (
          <div className="space-y-1.5 max-h-[240px] overflow-y-auto pr-1" data-testid="meeting-actions-tile-list">
            {items.slice(0, 8).map((ai) => (
              <button
                key={ai.id}
                onClick={() => nav(`/meetings/${ai.meeting_id}`)}
                className="w-full text-left rounded-xl bg-white/[0.02] hover:bg-white/[0.05] border border-white/[0.06] hover:border-sky-400/30 px-3 py-2 transition group"
                data-testid={`meeting-actions-tile-row-${ai.id}`}
              >
                <div className="flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-foreground/90 truncate">{ai.description}</div>
                    <div className="text-[10.5px] text-foreground/50 flex items-center gap-2 mt-0.5">
                      <CalendarClock className="h-3 w-3" />
                      <span className="truncate">{ai.meeting_title || "meeting"}</span>
                      {ai.due_date && <>
                        <span className="text-foreground/30">·</span>
                        <span>due {ai.due_date}</span>
                      </>}
                      {ai.assignee_name && (
                        <>
                          <span className="text-foreground/30">·</span>
                          <span className="flex items-center gap-1"><User className="h-2.5 w-2.5" />{ai.assignee_name}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <Badge
                    variant="outline"
                    className={`text-[10px] shrink-0 ${PRIORITY_STYLES[ai.priority] || PRIORITY_STYLES.P2}`}
                  >
                    {ai.priority || "P2"}
                  </Badge>
                </div>
              </button>
            ))}
            {count > 8 && (
              <div className="text-[10.5px] text-foreground/45 pt-1 text-center">
                +{count - 8} more — open Meetings to review the rest
              </div>
            )}
          </div>
        )}
      </GlassSurface>
    </motion.div>
  );
}
